import * as THREE from 'three';
import type { World } from '../../shared/types.js';
import { Heightfield } from '../../shared/heightfield.js';
import {
  LIVERY_COUNT,
  RAIL_CAR_SPECS,
  RAIL_CAR_TYPES,
  type RailCarType,
  type RailTraffic,
  type TrainPose,
} from '../../shared/railway.js';
import { crossingGear, LAMP_HEIGHT, LAMP_SPACING, PIVOT_HEIGHT, PIVOT_OFFSET, type CrossingPost } from './railways.js';

/**
 * Everything on the railway that moves: the trains, the booms at the crossings and the lamps
 * that flash on them. DECISIONS.md §95.
 *
 * Nothing here decides anything. Where a train is, and whether a crossing is shut, comes from
 * `RailTraffic` for a moment in server time; this only draws it. Every vehicle in the region
 * is one `BatchedMesh` — fourteen kinds of vehicle in up to three liveries each, one draw call
 * — and its lamps are drawn by the same shader, switched per instance through the batch's
 * colour channel, which is not otherwise used.
 */

export interface RailTrafficMeshes {
  objects: THREE.Object3D[];
  /** Puts every train, boom and lamp where it is at `t` seconds of server time. */
  update(t: number): void;
  setNight(amount: number): void;
  /**
   * How loud the nearest ringing crossing is at a point: 1 beside one, fading to nothing by
   * `BELL_REACH` metres. For the bell.
   */
  bellAt(x: number, z: number): number;
  /** How loud the nearest moving train is at a point, by distance and speed, 0..1. */
  rumbleAt(x: number, z: number): number;
  dispose(): void;
}

/** How far a crossing's bell carries, in metres. */
export const BELL_REACH = 90;
/** And a train going by. */
const RUMBLE_REACH = 160;
/** Up, in radians, for a boom that is open: not quite vertical, as a real one stands. */
const BOOM_UP = (84 * Math.PI) / 180;
/** Each lamp is lit for half of this, alternately. */
const FLASH_PERIOD = 1.1;

export function buildRailTraffic(world: World, traffic: RailTraffic, terrain?: Heightfield): RailTrafficMeshes | null {
  if (!world.rail || (traffic.isEmpty && !traffic.crossings.length)) return null;
  const objects: THREE.Object3D[] = [];
  const disposables: { dispose(): void }[] = [];
  const night = { value: 0 };

  // --- rolling stock ---------------------------------------------------------
  const geometries = new Map<string, THREE.BufferGeometry>();
  for (const type of RAIL_CAR_TYPES) {
    for (let livery = 0; livery < LIVERY_COUNT[type]; livery++) geometries.set(`${type}/${livery}`, buildCar(type, livery));
  }
  let vertexCount = 0;
  for (const g of geometries.values()) vertexCount += g.getAttribute('position').count;
  // As many cars as could ever be out at once: every trip's train, and the one behind it on a
  // run whose trip is longer than its period, plus the rakes in the yards.
  const capacity = Math.min(4000, Math.max(1, countCars(traffic)));
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.15 });
  patchTrainMaterial(material, night);
  const batch = new THREE.BatchedMesh(capacity, vertexCount, vertexCount, material);
  batch.name = 'trains';
  batch.castShadow = true;
  batch.receiveShadow = true;
  // The batch's own bounds are of wherever the trains were when it was built; each car is
  // culled on its own instead.
  batch.frustumCulled = false;
  batch.perObjectFrustumCulled = true;
  const geometryId = new Map<string, number>();
  for (const [key, g] of geometries) geometryId.set(key, batch.addGeometry(g));
  const anyId = geometryId.values().next().value as number;
  const instances: number[] = [];
  const hidden = new THREE.Color(0, 0, 1);
  for (let i = 0; i < capacity; i++) {
    const id = batch.addInstance(anyId);
    batch.setVisibleAt(id, false);
    // Writing a colour is what turns the per-instance channel on, which the lamps read.
    batch.setColorAt(id, hidden);
    instances.push(id);
  }
  objects.push(batch);
  disposables.push(batch, material, ...geometries.values());

  // --- crossings ---------------------------------------------------------------
  const ground = terrain ?? Heightfield.flat();
  const posts: CrossingPost[] = [];
  traffic.crossings.forEach((c, index) => posts.push(...crossingGear(c, ground, index)));
  const booms = posts.filter((p) => p.barrier);
  const lamps = posts.filter((p) => p.lights);

  const boomGeometry = new THREE.BoxGeometry(1, 0.1, 0.07);
  boomGeometry.translate(0.5, 0, 0);
  const boomMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6 });
  patchBoomMaterial(boomMaterial);
  const boomMesh = booms.length ? new THREE.InstancedMesh(boomGeometry, boomMaterial, booms.length) : null;
  if (boomMesh) {
    boomMesh.name = 'crossing-booms';
    boomMesh.castShadow = true;
    boomMesh.frustumCulled = false;
    objects.push(boomMesh);
  }
  disposables.push(boomGeometry, boomMaterial);

  const lensGeometry = new THREE.CircleGeometry(0.11, 12);
  const lensMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
  const lensMesh = lamps.length ? new THREE.InstancedMesh(lensGeometry, lensMaterial, lamps.length * 2) : null;
  if (lensMesh) {
    lensMesh.name = 'crossing-lamps';
    lensMesh.frustumCulled = false;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const forward = new THREE.Vector3(0, 0, 1);
    lamps.forEach((p, k) => {
      q.setFromUnitVectors(forward, new THREE.Vector3(p.fx, 0, p.fz));
      for (const side of [0, 1]) {
        const s = side === 0 ? -1 : 1;
        m.compose(
          new THREE.Vector3(p.x + p.fx * 0.105 + p.ix * s * LAMP_SPACING, p.y + LAMP_HEIGHT, p.z + p.fz * 0.105 + p.iz * s * LAMP_SPACING),
          q,
          new THREE.Vector3(1, 1, 1),
        );
        lensMesh.setMatrixAt(k * 2 + side, m);
        lensMesh.setColorAt(k * 2 + side, LENS_OFF);
      }
    });
    lensMesh.instanceMatrix.needsUpdate = true;
    objects.push(lensMesh);
  }
  disposables.push(lensGeometry, lensMaterial);

  // A halo round each lit lens, which is what reads as a light from down the street. The lens
  // alone is a few pixels at forty metres; by day the bloom barely finds it.
  const haloGeometry = new THREE.PlaneGeometry(0.9, 0.9);
  const haloMaterial = new THREE.MeshBasicMaterial({
    map: haloTexture(),
    color: new THREE.Color(0xff2a14).multiplyScalar(3),
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const haloMesh = lamps.length ? new THREE.InstancedMesh(haloGeometry, haloMaterial, lamps.length * 2) : null;
  if (haloMesh) {
    haloMesh.name = 'crossing-halos';
    haloMesh.frustumCulled = false;
    haloMesh.count = 0;
    objects.push(haloMesh);
    disposables.push(haloMesh);
  }
  disposables.push(haloGeometry, haloMaterial, haloMaterial.map!);
  const haloSpot = new THREE.Matrix4();
  const haloQ = new THREE.Quaternion();
  const haloS = new THREE.Vector3();
  if (boomMesh) disposables.push(boomMesh);
  if (lensMesh) disposables.push(lensMesh);

  // --- per frame -----------------------------------------------------------------
  const matrix = new THREE.Matrix4();
  const basisX = new THREE.Vector3();
  const basisY = new THREE.Vector3();
  const basisZ = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const state = new THREE.Color();
  const lens = new THREE.Color();
  let ringing: { x: number; z: number }[] = [];
  let moving: TrainPose[] = [];

  // Headlamp halos, the same trick as the crossing lamps': what makes a train coming at you
  // down a dark line read as one from four hundred metres.
  const headGeometry = new THREE.PlaneGeometry(1.1, 1.1);
  const headMaterial = new THREE.MeshBasicMaterial({
    map: haloTexture(),
    color: new THREE.Color(1, 0.95, 0.85).multiplyScalar(2.5),
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  const headCapacity = Math.max(2, traffic.tripCount * 4);
  const headMesh = new THREE.InstancedMesh(headGeometry, headMaterial, headCapacity);
  headMesh.name = 'train-headlamps';
  headMesh.frustumCulled = false;
  headMesh.count = 0;
  objects.push(headMesh);
  disposables.push(headMesh, headGeometry, headMaterial, headMaterial.map!);
  const lampAt = new THREE.Vector3();
  const lampFacing = new THREE.Quaternion();
  const lampOut = new THREE.Vector3();
  const lampMatrix = new THREE.Matrix4();
  const lampScale = new THREE.Vector3(1, 1, 1);
  const zAxis = new THREE.Vector3(0, 0, 1);

  const placeTrains = (trains: TrainPose[]) => {
    let used = 0;
    let heads = 0;
    for (const train of trains) {
      const last = train.cars.length - 1;
      train.cars.forEach((car, k) => {
        if (used >= instances.length) return;
        const id = instances[used++];
        batch.setGeometryIdAt(id, geometryId.get(`${car.type}/${Math.min(car.livery, LIVERY_COUNT[car.type] - 1)}`)!);
        basisX.set(car.dx, car.slope, car.dz).normalize();
        basisY.copy(up).addScaledVector(basisX, -basisX.y).normalize();
        basisZ.crossVectors(basisX, basisY);
        matrix.makeBasis(basisX, basisY, basisZ).setPosition(car.x, car.y, car.z);
        batch.setMatrixAt(id, matrix);
        lampState(train, k, last, car, state);
        batch.setColorAt(id, state);
        batch.setVisibleAt(id, true);
        if (k === train.lead && train.travel && heads + 2 <= headCapacity) {
          const spec = RAIL_CAR_SPECS[car.type];
          const end = car.dx * train.travel.x + car.dz * train.travel.z >= 0 ? 1 : -1;
          const low = car.type.startsWith('tram') ? 0.95 : car.type.startsWith('narrow') ? 1.35 : 1.75;
          lampOut.copy(basisX).multiplyScalar(end);
          lampFacing.setFromUnitVectors(zAxis, lampOut);
          for (const side of [-1, 1]) {
            lampAt.set(end * (spec.length / 2 + 0.15), low, side * (spec.width / 2 - 0.4)).applyMatrix4(matrix);
            lampMatrix.compose(lampAt, lampFacing, lampScale);
            headMesh.setMatrixAt(heads++, lampMatrix);
          }
        }
      });
    }
    for (let i = used; i < instances.length; i++) batch.setVisibleAt(instances[i], false);
    headMesh.count = heads;
    headMesh.instanceMatrix.needsUpdate = true;
  };

  return {
    objects,
    update(t: number) {
      const trains = traffic.trainsAt(t);
      placeTrains(trains);
      moving = trains.filter((train) => !train.parked);
      ringing = [];
      // One state per crossing, shared by its posts.
      const states = traffic.crossings.map((_, index) => traffic.crossingAt(index, t));
      states.forEach((s, index) => {
        if (s.lights) ringing.push({ x: traffic.crossings[index].x, z: traffic.crossings[index].z });
      });
      if (boomMesh) {
        booms.forEach((p, k) => {
          const s = states[p.crossing];
          const angle = BOOM_UP * (1 - s.barrier);
          // Along the boom when it is down: from the kerb into the road.
          basisX.set(p.ix * Math.cos(angle), Math.sin(angle), p.iz * Math.cos(angle));
          basisY.set(-p.ix * Math.sin(angle), Math.cos(angle), -p.iz * Math.sin(angle));
          basisZ.crossVectors(basisX, basisY);
          matrix.makeBasis(basisX.multiplyScalar(p.arm), basisY, basisZ);
          matrix.setPosition(p.x + p.ix * PIVOT_OFFSET, p.y + PIVOT_HEIGHT, p.z + p.iz * PIVOT_OFFSET);
          boomMesh.setMatrixAt(k, matrix);
        });
        boomMesh.instanceMatrix.needsUpdate = true;
      }
      if (lensMesh) {
        let halos = 0;
        lamps.forEach((p, k) => {
          const s = states[p.crossing];
          const phase = s.lights ? (s.since % FLASH_PERIOD) / FLASH_PERIOD : -1;
          for (const side of [0, 1]) {
            const on = s.lights && (side === 0 ? phase < 0.5 : phase >= 0.5);
            lens.copy(on ? LENS_ON : LENS_OFF);
            lensMesh.setColorAt(k * 2 + side, lens);
            if (on && haloMesh) {
              lensMesh.getMatrixAt(k * 2 + side, haloSpot);
              haloSpot.decompose(basisX, haloQ, haloS);
              // A little in front of the lens, so it never sinks into the backplate.
              basisX.x += p.fx * 0.04;
              basisX.z += p.fz * 0.04;
              haloSpot.compose(basisX, haloQ, haloS.setScalar(1));
              haloMesh.setMatrixAt(halos++, haloSpot);
            }
          }
        });
        if (lensMesh.instanceColor) lensMesh.instanceColor.needsUpdate = true;
        if (haloMesh) {
          haloMesh.count = halos;
          haloMesh.instanceMatrix.needsUpdate = true;
          haloMaterial.opacity = 0.35 + 0.65 * night.value;
        }
      }
    },
    setNight(amount: number) {
      night.value = Math.max(0, Math.min(1, amount));
      headMaterial.opacity = 0.15 + 0.85 * night.value;
    },
    bellAt(x: number, z: number) {
      let best = 0;
      for (const r of ringing) {
        const d = Math.hypot(r.x - x, r.z - z);
        if (d < BELL_REACH) best = Math.max(best, 1 - d / BELL_REACH);
      }
      return best;
    },
    rumbleAt(x: number, z: number) {
      let best = 0;
      for (const train of moving) {
        for (const car of train.cars) {
          const d = Math.hypot(car.x - x, car.z - z);
          if (d >= RUMBLE_REACH) continue;
          const speed = Math.min(1, Math.hypot(car.vx, car.vz) / 15);
          best = Math.max(best, (1 - d / RUMBLE_REACH) ** 2 * speed);
        }
      }
      return best;
    },
    dispose() {
      for (const d of disposables) d.dispose();
    },
  };
}

/** A soft round falloff, white in the middle, for the halos. */
function haloTexture(): THREE.Texture {
  const size = 32;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x + 0.5 - size / 2, y + 0.5 - size / 2) / (size / 2);
      const v = Math.max(0, 1 - d) ** 2.2;
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(v * 255);
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/** A lit lens is several times white, so the bloom finds it by day as well as by night. */
const LENS_ON = new THREE.Color(1, 0.06, 0.03).multiplyScalar(7);
const LENS_OFF = new THREE.Color(0.1, 0.015, 0.012);

/**
 * Which of a car's lamps are on: white at the end leading the train, red at the end trailing
 * it, nothing in between. Stood at a platform a train keeps the lamps it arrived with —
 * `travel` is the direction it is about to move in, which is the same thing.
 *
 * Encoded in the batch colour: red for the car's own front, green for its back; 1 is white,
 * 0.5 red, 0 off.
 */
function lampState(train: TrainPose, k: number, last: number, car: { dx: number; dz: number }, out: THREE.Color): void {
  out.setRGB(0, 0, 1);
  if (!train.travel) return;
  const forward = car.dx * train.travel.x + car.dz * train.travel.z >= 0;
  const tail = train.lead === 0 ? last : 0;
  if (k === train.lead) {
    if (forward) out.r = 1;
    else out.g = 1;
  }
  if (k === tail) {
    if (forward) out.g = 0.5;
    else out.r = 0.5;
  }
}

function countCars(traffic: RailTraffic): number {
  // Two of every running train is generous; the exact figure would need the timetable's
  // internals, and an unused instance costs a few bytes and nothing on the GPU.
  let n = 0;
  for (const train of traffic.trainsAt(0)) n += train.cars.length;
  for (const train of traffic.trainsAt(97)) n += train.cars.length;
  return n * 2 + traffic.tripCount * 30 + 16;
}

// ---------------------------------------------------------------------------
// shaders
// ---------------------------------------------------------------------------

function patchTrainMaterial(material: THREE.MeshStandardMaterial, night: { value: number }): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.trainNight = night;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute float trainGlow;\nvarying float vTrainGlow;\nvarying vec2 vTrainLamp;',
      )
      // The batch colour is not a tint here: it says which lamps are lit. The whole chunk is
      // replaced, because its includes are not expanded yet when this runs.
      .replace(
        '#include <color_vertex>',
        'vColor = vec3( 1.0 );\n#ifdef USE_COLOR\n\tvColor *= color;\n#endif\n' +
          '#ifdef USE_BATCHING_COLOR\n\tvTrainLamp = getBatchingColor( getIndirectIndex( gl_DrawID ) ).xy;\n' +
          '#else\n\tvTrainLamp = vec2( 0.0 );\n#endif',
      )
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n\tvTrainGlow = trainGlow;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float trainNight;\nvarying float vTrainGlow;\nvarying vec2 vTrainLamp;',
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
	if ( vTrainGlow > 0.5 && vTrainGlow < 1.5 ) {
		// Windows: the saloon lights, on after dark.
		totalEmissiveRadiance += vec3( 1.0, 0.84, 0.6 ) * 0.5 * trainNight;
	} else if ( vTrainGlow > 1.5 ) {
		float lamp = vTrainGlow < 2.5 ? vTrainLamp.x : vTrainLamp.y;
		if ( lamp > 0.75 ) {
			diffuseColor.rgb *= 0.2;
			totalEmissiveRadiance += vec3( 1.0, 0.96, 0.86 ) * mix( 3.0, 9.0, trainNight );
		} else if ( lamp > 0.25 ) {
			diffuseColor.rgb *= 0.2;
			totalEmissiveRadiance += vec3( 1.0, 0.05, 0.03 ) * mix( 2.0, 6.0, trainNight );
		}
	}`,
      );
  };
  material.customProgramCacheKey = () => 'trains';
}

/** Red and white in half-metre bands, whatever the boom's length — read off the instance's own scale. */
function patchBoomMaterial(material: THREE.MeshStandardMaterial): void {
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying float vBoomAlong;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n#ifdef USE_INSTANCING\n\tvBoomAlong = position.x * length( instanceMatrix[0].xyz );\n#else\n\tvBoomAlong = position.x;\n#endif',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vBoomAlong;')
      .replace(
        '#include <color_fragment>',
        '#include <color_fragment>\n\tfloat boomBand = step( 0.5, fract( vBoomAlong / 1.0 ) );\n' +
          '\tdiffuseColor.rgb = mix( vec3( 0.75, 0.08, 0.06 ), vec3( 0.86, 0.86, 0.84 ), boomBand );',
      );
  };
  material.customProgramCacheKey = () => 'crossing-boom';
}

// ---------------------------------------------------------------------------
// rolling stock, built from boxes
// ---------------------------------------------------------------------------

interface Livery {
  body: number;
  skirt: number;
  band: number;
  roof: number;
  /** The cab end, where it differs: a yellow warning panel, a darker nose. */
  front?: number;
  /** Containers on a flat wagon. */
  cargo?: number[];
}

const LIVERIES: Record<RailCarType, Livery[]> = {
  'loco-electric': [
    { body: 0x2c5aa0, skirt: 0x1d3f73, band: 0xe8e2c8, roof: 0x5d6266 },
    { body: 0x6f7c6c, skirt: 0x394538, band: 0xc22e2e, roof: 0x5d6266, front: 0xe0b020 },
    { body: 0xc0262c, skirt: 0x2a2a2a, band: 0xe6e6e6, roof: 0x55595c },
  ],
  'loco-diesel': [
    { body: 0xdf782d, skirt: 0x2a2a2a, band: 0xeadfba, roof: 0x55595c },
    { body: 0x3e6b3a, skirt: 0x222222, band: 0xe0c040, roof: 0x4a4e50 },
  ],
  'emu-cab': [
    { body: 0xe9e7e2, skirt: 0xc8102e, band: 0x1f3b73, roof: 0x8a8d90, front: 0xc8102e },
    { body: 0xeceae6, skirt: 0x2f7d32, band: 0xb8c72a, roof: 0x8a8d90, front: 0xf2c500 },
    { body: 0xe3cc72, skirt: 0x2c4f8a, band: 0xb22222, roof: 0x6c6f72, front: 0x2c4f8a },
  ],
  'emu-mid': [],
  dmu: [
    { body: 0xe9e7e2, skirt: 0xc8102e, band: 0x1f3b73, roof: 0x8a8d90, front: 0xc8102e },
    { body: 0xf0d23a, skirt: 0x3c7a3a, band: 0x3c7a3a, roof: 0x7a7d80, front: 0x3c7a3a },
  ],
  coach: [
    { body: 0xe5e5e3, skirt: 0x2a3f6f, band: 0xd0212b, roof: 0x7a7d80 },
    { body: 0x2d4a7a, skirt: 0x2d4a7a, band: 0xd9d9d6, roof: 0x555a5e },
  ],
  'wagon-box': [
    { body: 0x8a3b2a, skirt: 0x2a2624, band: 0x7a3426, roof: 0x6a6a68 },
    { body: 0x3f5e45, skirt: 0x2a2624, band: 0x37523c, roof: 0x6a6a68 },
    { body: 0x7d8085, skirt: 0x2a2624, band: 0x6d7075, roof: 0x8a8c8e },
  ],
  'wagon-tank': [
    { body: 0x2a2a2a, skirt: 0x1f1f1f, band: 0xd8d8d0, roof: 0x2a2a2a },
    { body: 0xb9bcbf, skirt: 0x2a2624, band: 0xd0602a, roof: 0xb9bcbf },
    { body: 0x7e5538, skirt: 0x2a2624, band: 0x6e4a30, roof: 0x7e5538 },
  ],
  'wagon-hopper': [
    { body: 0x7a4a30, skirt: 0x2a2624, band: 0x6a3e28, roof: 0x7a4a30 },
    { body: 0x9a9da0, skirt: 0x2a2624, band: 0x85888b, roof: 0x9a9da0 },
    { body: 0xb08a3a, skirt: 0x2a2624, band: 0x9a7830, roof: 0xb08a3a },
  ],
  'wagon-flat': [
    { body: 0x4a4440, skirt: 0x2a2624, band: 0x4a4440, roof: 0, cargo: [0x2b5d9a, 0xb03030] },
    { body: 0x4a4440, skirt: 0x2a2624, band: 0x4a4440, roof: 0, cargo: [0x3a7a4a, 0xd8a020] },
  ],
  'tram-cab': [
    { body: 0xf0f0ee, skirt: 0x1d3c78, band: 0x1d3c78, roof: 0xb8bcc0, front: 0x1d3c78 },
    { body: 0xd1262b, skirt: 0x2b2b2b, band: 0xf0f0ee, roof: 0xb8bcc0 },
    { body: 0xf2c81e, skirt: 0x2b2b2b, band: 0xf0f0ee, roof: 0xb8bcc0 },
  ],
  'tram-mid': [],
  'narrow-loco': [{ body: 0x2f5b3a, skirt: 0x222222, band: 0xc23a2a, roof: 0x3a3e40 }],
  'narrow-coach': [{ body: 0x2f5b3a, skirt: 0x222222, band: 0xd8cfa8, roof: 0x3a3e40 }],
};
LIVERIES['emu-mid'] = LIVERIES['emu-cab'];
LIVERIES['tram-mid'] = LIVERIES['tram-cab'];

const GLASS = 0x1d2329;
const UNDERFRAME = 0x37373a;
const BOGIE = 0x2b2b2d;
const DOOR_SHADE = 0.82;

const GLOW_WINDOW = 1;
const GLOW_FRONT_LAMP = 2;
const GLOW_BACK_LAMP = 3;

/** What sort of body a type has, and where its doors and windows go. */
function buildCar(type: RailCarType, liveryIndex: number): THREE.BufferGeometry {
  const spec = RAIL_CAR_SPECS[type];
  const livery = LIVERIES[type][Math.min(liveryIndex, LIVERIES[type].length - 1)];
  const g = new CarBuilder();
  const L = spec.length;
  const W = spec.width;
  const H = spec.height;
  const x0 = -L / 2;
  const x1 = L / 2;
  const tram = type === 'tram-cab' || type === 'tram-mid';
  const narrow = type === 'narrow-loco' || type === 'narrow-coach';
  const floor = tram ? 0.35 : narrow ? 0.75 : 1.15;

  // Running gear.
  if (!tram) {
    const bogieAt = L / 2 - L * 0.17;
    const bogieLen = narrow ? 1.6 : 2.7;
    for (const s of [-1, 1]) g.box(s * bogieAt - bogieLen / 2, s * bogieAt + bogieLen / 2, 0.05, floor - 0.2, -W / 2 + 0.35, W / 2 - 0.35, BOGIE);
    g.box(x0 + 0.6, x1 - 0.6, floor - 0.25, floor, -W / 2 + 0.2, W / 2 - 0.2, UNDERFRAME);
  } else {
    g.box(x0 + 0.4, x1 - 0.4, 0.05, floor, -W / 2 + 0.25, W / 2 - 0.25, BOGIE);
  }

  const cab = type === 'loco-electric' || type === 'loco-diesel' || type === 'emu-cab' || type === 'dmu' || type === 'tram-cab' || type === 'narrow-loco';
  const cabBothEnds = type === 'loco-electric' || type === 'loco-diesel';

  switch (type) {
    case 'wagon-tank': {
      g.box(x0 + 0.3, x1 - 0.3, floor, floor + 0.25, -W / 2 + 0.1, W / 2 - 0.1, livery.skirt);
      g.cylinder(x0 + 0.6, x1 - 0.6, floor + 1.45, 1.35, livery.body, 12);
      g.box(-0.5, 0.5, floor + 2.8, floor + 3.0, -0.5, 0.5, livery.band);
      g.box(x0 + 1.2, x0 + 1.5, floor + 0.25, floor + 1.4, -W / 2 + 0.05, W / 2 - 0.05, livery.band);
      g.box(x1 - 1.5, x1 - 1.2, floor + 0.25, floor + 1.4, -W / 2 + 0.05, W / 2 - 0.05, livery.band);
      break;
    }
    case 'wagon-hopper': {
      const top = H;
      // Sloped ends, a body that narrows down towards the doors underneath.
      g.prism(
        [[x0 + 0.4, floor + 0.2], [x1 - 0.4, floor + 0.2], [x1 - 0.2, top - 0.3], [x0 + 0.2, top - 0.3]],
        W / 2 - 0.05,
        livery.body,
      );
      g.box(x0 + 0.2, x1 - 0.2, top - 0.3, top, -W / 2, W / 2, livery.band);
      for (let k = 1; k < 6; k++) {
        const x = x0 + (L * k) / 6;
        g.box(x - 0.08, x + 0.08, floor + 0.3, top - 0.3, -W / 2 - 0.04, W / 2 + 0.04, livery.band);
      }
      break;
    }
    case 'wagon-flat': {
      g.box(x0 + 0.2, x1 - 0.2, floor, floor + 0.3, -W / 2 + 0.05, W / 2 - 0.05, livery.body);
      const cargo = livery.cargo ?? [0x777777];
      const twenty = (liveryIndex % 2) === 0;
      if (twenty) {
        for (const [k, c] of [[-1, cargo[0]], [1, cargo[1] ?? cargo[0]]] as const) {
          const cx = k * 4.9;
          g.container(cx - 3.0, cx + 3.0, floor + 0.3, floor + 2.9, 1.22, c);
        }
      } else {
        g.container(-6.1, 6.1, floor + 0.3, floor + 2.9, 1.22, cargo[0]);
      }
      break;
    }
    case 'wagon-box': {
      g.box(x0 + 0.2, x1 - 0.2, floor, H - 0.15, -W / 2, W / 2, livery.body);
      g.roof(x0 + 0.2, x1 - 0.2, H - 0.15, H, W / 2, livery.roof);
      for (let k = 0; k <= 10; k++) {
        const x = x0 + 0.4 + ((L - 0.8) * k) / 10;
        g.side(x - 0.06, x + 0.06, floor + 0.1, H - 0.2, W / 2 + 0.02, shade(livery.body, 0.75), 0);
      }
      g.side(-1.6, 1.6, floor + 0.2, H - 0.35, W / 2 + 0.03, shade(livery.body, 0.88), 0);
      break;
    }
    default: {
      // A passenger body or a locomotive: bands of livery up the side, a cab nose where
      // there is one, windows and doors on top of it.
      const top = H - 0.3;
      const nose = tram ? 0.55 : type === 'emu-cab' || type === 'dmu' ? 0.9 : narrow ? 0.2 : 0.45;
      const breakY = top - (tram ? 1.5 : 1.25);
      const frontX = (y: number) => (cab && y > breakY ? x1 - nose * (y - breakY) / (top - breakY) : x1);
      const backX = (y: number) => (cabBothEnds && y > breakY ? x0 + nose * (y - breakY) / (top - breakY) : x0);
      const skirtTop = floor + (tram ? 0.55 : 0.45);
      const bandLo = breakY - 0.12;
      const bands: [number, number, number][] = [
        [floor, skirtTop, livery.skirt],
        [skirtTop, bandLo, livery.body],
        [bandLo, breakY, livery.band],
        [breakY, top, livery.body],
      ];
      for (const [ya, yb, color] of bands) g.layer(ya, yb, backX, frontX, W / 2, color, cab ? livery.front : undefined, cabBothEnds ? livery.front : undefined);
      g.roof(backX(top), frontX(top), top, H, W / 2, livery.roof);

      // Windows down both sides.
      const winLo = breakY + (tram ? -0.55 : 0.05);
      const winHi = top - 0.25;
      const doorW = tram ? 1.3 : 1.3;
      const doors: number[] = [];
      if (type === 'coach') doors.push(x0 + 1.2, x1 - 1.2);
      else if (type === 'emu-cab' || type === 'dmu') doors.push(x0 + 2.2, x1 - 4.8);
      else if (type === 'emu-mid') doors.push(x0 + 2.4, x1 - 2.4);
      else if (tram) doors.push(type === 'tram-cab' ? -0.5 : 0);
      else if (type === 'narrow-coach') doors.push(x0 + 0.9, x1 - 0.9);
      const passenger = RAIL_CAR_SPECS[type].windows;
      if (passenger) {
        const start = x0 + (cab ? 0.6 : 0.5);
        const end = cab ? x1 - nose - 2.6 : x1 - 0.5;
        const pitch = tram ? 1.35 : narrow ? 1.2 : type === 'coach' ? 2.0 : 1.6;
        for (let x = start; x + pitch * 0.75 <= end; x += pitch) {
          if (doors.some((d) => Math.abs(x + pitch * 0.4 - d) < doorW / 2 + pitch * 0.45)) continue;
          g.side(x + pitch * 0.08, x + pitch * 0.88, winLo, winHi, W / 2 + 0.012, GLASS, GLOW_WINDOW);
        }
      } else if (!cab) {
        // nothing
      } else {
        // A locomotive: a row of small engine-room windows and grilles.
        for (let k = 0; k < 4; k++) {
          const x = -L / 4 + (k * L) / 8;
          g.side(x, x + 0.9, breakY + 0.35, top - 0.35, W / 2 + 0.012, GLASS, 0);
        }
      }
      for (const d of doors) {
        g.side(d - doorW / 2, d + doorW / 2, floor + 0.1, top - 0.15, W / 2 + 0.008, shade(livery.body, DOOR_SHADE), 0);
        if (passenger) g.side(d - doorW / 2 + 0.15, d + doorW / 2 - 0.15, winLo + 0.1, winHi, W / 2 + 0.016, GLASS, GLOW_WINDOW);
      }

      if (cab) {
        // The windscreen across the nose, and the lamps under it.
        g.windscreen(frontX, breakY + 0.1, top - 0.12, W / 2 - 0.15, 1);
        g.lamps(x1 + 0.02, floor + 0.6, W / 2 - 0.4, top - 0.35, 1, GLOW_FRONT_LAMP);
      }
      if (cabBothEnds) {
        g.windscreen(backX, breakY + 0.1, top - 0.12, W / 2 - 0.15, -1);
        g.lamps(x0 - 0.02, floor + 0.6, W / 2 - 0.4, top - 0.35, -1, GLOW_BACK_LAMP);
      } else {
        // Tail lamps on the plain end of anything.
        g.lamps(x0 - 0.02, floor + 0.6, W / 2 - 0.4, null, -1, GLOW_BACK_LAMP);
      }

      // A pantograph where one would be: on every electric loco, on the middle car of a unit,
      // and on the back of a tram's cab section.
      if (type === 'loco-electric' || type === 'emu-mid' || type === 'tram-cab' || type === 'tram-mid') {
        g.pantograph(type === 'tram-cab' ? -1.5 : type === 'loco-electric' ? x1 - 4 : 0, H);
        if (type === 'loco-electric') g.pantograph(x0 + 4, H);
      }
      if (tram) g.box(-2, 2, H, H + 0.35, -0.8, 0.8, livery.roof);
      break;
    }
  }

  // The plain ends of anything that is not a cab: wagons and coaches carry tail lamps too.
  if (type.startsWith('wagon')) {
    g.lamps(x0 - 0.05, floor + 0.15, W / 2 - 0.3, null, -1, GLOW_BACK_LAMP, 0.12);
    g.lamps(x1 + 0.05, floor + 0.15, W / 2 - 0.3, null, 1, GLOW_FRONT_LAMP, 0.12);
  }
  return g.toGeometry();
}

type V3 = [number, number, number];

class CarBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly glows: number[] = [];
  private readonly c = new THREE.Color();

  tri(a: V3, b: V3, cc: V3, color: number, glow = 0): void {
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = cc[0] - a[0];
    const vy = cc[1] - a[1];
    const vz = cc[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len < 1e-9) return;
    nx /= len;
    ny /= len;
    nz /= len;
    this.c.setHex(color, THREE.SRGBColorSpace);
    for (const p of [a, b, cc]) {
      this.positions.push(p[0], p[1], p[2]);
      this.normals.push(nx, ny, nz);
      this.colors.push(this.c.r, this.c.g, this.c.b);
      this.glows.push(glow);
    }
  }

  /** A quad with its front towards `facing`, whatever order the corners came in. */
  quad(a: V3, b: V3, c: V3, d: V3, facing: V3, color: number, glow = 0): void {
    const n = [
      (b[1] - a[1]) * (c[2] - a[2]) - (b[2] - a[2]) * (c[1] - a[1]),
      (b[2] - a[2]) * (c[0] - a[0]) - (b[0] - a[0]) * (c[2] - a[2]),
      (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]),
    ];
    if (n[0] * facing[0] + n[1] * facing[1] + n[2] * facing[2] >= 0) {
      this.tri(a, b, c, color, glow);
      this.tri(a, c, d, color, glow);
    } else {
      this.tri(a, c, b, color, glow);
      this.tri(a, d, c, color, glow);
    }
  }

  box(xa: number, xb: number, ya: number, yb: number, za: number, zb: number, color: number, glow = 0): void {
    const p = (x: number, y: number, z: number): V3 => [x, y, z];
    this.quad(p(xb, ya, za), p(xb, yb, za), p(xb, yb, zb), p(xb, ya, zb), [1, 0, 0], color, glow);
    this.quad(p(xa, ya, za), p(xa, yb, za), p(xa, yb, zb), p(xa, ya, zb), [-1, 0, 0], color, glow);
    this.quad(p(xa, ya, zb), p(xb, ya, zb), p(xb, yb, zb), p(xa, yb, zb), [0, 0, 1], color, glow);
    this.quad(p(xa, ya, za), p(xb, ya, za), p(xb, yb, za), p(xa, yb, za), [0, 0, -1], color, glow);
    this.quad(p(xa, yb, za), p(xb, yb, za), p(xb, yb, zb), p(xa, yb, zb), [0, 1, 0], color, glow);
  }

  /**
   * One horizontal band of a body, between two heights, whose ends may lean in (a cab nose):
   * `back(y)` and `front(y)` give the ends' x at a height.
   */
  layer(
    ya: number, yb: number, back: (y: number) => number, front: (y: number) => number, hw: number, color: number,
    frontColor?: number, backColor?: number,
  ): void {
    const fa = front(ya);
    const fb = front(yb);
    const ba = back(ya);
    const bb = back(yb);
    for (const s of [-1, 1]) {
      const z = s * hw;
      this.quad([ba, ya, z], [fa, ya, z], [fb, yb, z], [bb, yb, z], [0, 0, s], color);
    }
    this.quad([fa, ya, -hw], [fa, ya, hw], [fb, yb, hw], [fb, yb, -hw], [1, (fa - fb) / Math.max(yb - ya, 1e-3), 0], frontColor ?? color);
    this.quad([ba, ya, -hw], [ba, ya, hw], [bb, yb, hw], [bb, yb, -hw], [-1, (bb - ba) / Math.max(yb - ya, 1e-3), 0], backColor ?? color);
  }

  /** A shallow curved roof: a flat middle and a bevel down to each side. */
  roof(xa: number, xb: number, ya: number, yb: number, hw: number, color: number): void {
    const edge = Math.min(0.55, hw * 0.4);
    for (const s of [-1, 1]) {
      this.quad([xa, ya, s * hw], [xb, ya, s * hw], [xb, yb, s * (hw - edge)], [xa, yb, s * (hw - edge)], [0, 1, s * 1.2], color);
      this.quad([xa, ya, s * hw], [xa, yb, s * (hw - edge)], [xa, yb, 0], [xa, ya, 0], [-1, 0, 0], color);
      this.quad([xb, ya, s * hw], [xb, yb, s * (hw - edge)], [xb, yb, 0], [xb, ya, 0], [1, 0, 0], color);
    }
    this.quad([xa, yb, -(hw - edge)], [xb, yb, -(hw - edge)], [xb, yb, hw - edge], [xa, yb, hw - edge], [0, 1, 0], color);
  }

  /** A panel on both sides, a hair proud of the body: a window, a door, a rib. */
  side(xa: number, xb: number, ya: number, yb: number, z: number, color: number, glow: number): void {
    for (const s of [-1, 1]) {
      this.quad([xa, ya, s * z], [xb, ya, s * z], [xb, yb, s * z], [xa, yb, s * z], [0, 0, s], color, glow);
    }
  }

  /** A prism from a side profile, `hw` either side of the middle. */
  prism(profile: [number, number][], hw: number, color: number): void {
    const n = profile.length;
    for (const s of [-1, 1]) {
      for (let k = 1; k < n - 1; k++) {
        const a: V3 = [profile[0][0], profile[0][1], s * hw];
        const b: V3 = [profile[k][0], profile[k][1], s * hw];
        const c: V3 = [profile[k + 1][0], profile[k + 1][1], s * hw];
        this.quad(a, b, c, c, [0, 0, s], color);
      }
    }
    for (let k = 0; k < n; k++) {
      const [ax, ay] = profile[k];
      const [bx, by] = profile[(k + 1) % n];
      const out: V3 = [by - ay, -(bx - ax), 0];
      // Outward is away from the profile's middle.
      const mx = profile.reduce((acc, p) => acc + p[0], 0) / n;
      const my = profile.reduce((acc, p) => acc + p[1], 0) / n;
      const flip = out[0] * ((ax + bx) / 2 - mx) + out[1] * ((ay + by) / 2 - my) < 0 ? -1 : 1;
      this.quad([ax, ay, -hw], [bx, by, -hw], [bx, by, hw], [ax, ay, hw], [out[0] * flip, out[1] * flip, 0], color);
    }
  }

  /** A horizontal cylinder along x, for a tank. */
  cylinder(xa: number, xb: number, cy: number, r: number, color: number, segments: number): void {
    for (let k = 0; k < segments; k++) {
      const a0 = (k / segments) * Math.PI * 2;
      const a1 = ((k + 1) / segments) * Math.PI * 2;
      const p = (x: number, a: number): V3 => [x, cy + Math.sin(a) * r, Math.cos(a) * r];
      const mid = (a0 + a1) / 2;
      this.quad(p(xa, a0), p(xb, a0), p(xb, a1), p(xa, a1), [0, Math.sin(mid), Math.cos(mid)], color);
      this.quad([xa, cy, 0], p(xa, a0), p(xa, a1), p(xa, a1), [-1, 0, 0], color);
      this.quad([xb, cy, 0], p(xb, a0), p(xb, a1), p(xb, a1), [1, 0, 0], color);
    }
  }

  /** A shipping container, ribbed. */
  container(xa: number, xb: number, ya: number, yb: number, hw: number, color: number): void {
    this.box(xa, xb, ya, yb, -hw, hw, color);
    const n = Math.max(4, Math.round((xb - xa) / 0.6));
    for (let k = 1; k < n; k++) {
      const x = xa + ((xb - xa) * k) / n;
      this.side(x - 0.05, x + 0.05, ya + 0.1, yb - 0.1, hw + 0.015, shade(color, 0.78), 0);
    }
  }

  /** The cab glass on a nose, leaning with it. */
  windscreen(endX: (y: number) => number, ya: number, yb: number, hw: number, dir: 1 | -1): void {
    const xa = endX(ya) + dir * 0.015;
    const xb = endX(yb) + dir * 0.015;
    this.quad([xa, ya, -hw], [xa, ya, hw], [xb, yb, hw], [xb, yb, -hw], [dir, Math.abs(xa - xb), 0], GLASS);
  }

  /** Two lamps low on an end, and one high in the middle if `high` is given. */
  lamps(x: number, low: number, spread: number, high: number | null, dir: 1 | -1, glow: number, size = 0.22): void {
    const h = size / 2;
    const at = (z: number, y: number) =>
      this.quad([x, y - h, z - h], [x, y - h, z + h], [x, y + h, z + h], [x, y + h, z - h], [dir, 0, 0], 0x303030, glow);
    at(-spread, low);
    at(spread, low);
    if (high !== null) at(0, high);
  }

  /** A pantograph folded half up: a frame, two arms and the pan. */
  pantograph(x: number, roofY: number): void {
    const dark = 0x2e3236;
    this.box(x - 0.9, x + 0.9, roofY, roofY + 0.12, -0.6, 0.6, dark);
    this.bar([x - 0.6, roofY + 0.12, 0], [x + 0.25, roofY + 0.85, 0], 0.06, dark);
    this.bar([x + 0.25, roofY + 0.85, 0], [x - 0.2, roofY + 1.55, 0], 0.05, dark);
    this.box(x - 0.35, x - 0.05, roofY + 1.53, roofY + 1.6, -0.95, 0.95, dark);
  }

  /** A thin square bar from p to q, in the x–y plane. */
  bar(p: V3, q: V3, t: number, color: number): void {
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    const len = Math.hypot(dx, dy) || 1;
    const n1: V3 = [-dy / len, dx / len, 0];
    const n2: V3 = [0, 0, 1];
    const c = (a: V3, s1: number, s2: number): V3 => [
      a[0] + (n1[0] * s1 + n2[0] * s2) * t,
      a[1] + (n1[1] * s1 + n2[1] * s2) * t,
      a[2] + (n1[2] * s1 + n2[2] * s2) * t,
    ];
    for (const s1 of [-1, 1]) this.quad(c(p, s1, -1), c(q, s1, -1), c(q, s1, 1), c(p, s1, 1), [n1[0] * s1, n1[1] * s1, 0], color);
    for (const s2 of [-1, 1]) this.quad(c(p, -1, s2), c(q, -1, s2), c(q, 1, s2), c(p, 1, s2), [0, 0, s2], color);
  }

  toGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    geometry.setAttribute('trainGlow', new THREE.Float32BufferAttribute(this.glows, 1));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    return geometry;
  }
}

function shade(color: number, k: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * k));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * k));
  const b = Math.min(255, Math.round((color & 0xff) * k));
  return (r << 16) | (g << 8) | b;
}
